import { useApolloClient, useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AddUserDocument,
  type AddUserMutation,
  type AddUserMutationVariables,
  GetUsersDocument,
} from "~/features/admin/users/gqls";
import type { ProductRoleAssignment, UserFormData } from "~/features/admin/users/types";
import { translateError } from "~/lib/errors";

import { useRetryUserLoginPreparation } from "./useRetryUserLoginPreparation";

interface UseUserCreateOptions {
  onSuccess?: (data: UserFormData) => void;
}

export function useUserCreate({ onSuccess }: UseUserCreateOptions = {}) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { notifySaved } = useRetryUserLoginPreparation();
  const [processing, setProcessing] = useState(false);
  const [addUser] = useMutation<AddUserMutation, AddUserMutationVariables>(AddUserDocument, {
    refetchQueries: [GetUsersDocument],
  });

  const [selectedRoles, setSelectedRoles] = useState<Map<string, ProductRoleAssignment>>(new Map());

  const setRoleForProduct = useCallback((productId: string, roleId: string | null) => {
    setSelectedRoles((prev) => {
      const next = new Map(prev);
      if (roleId === null || roleId === "") {
        next.delete(productId);
      } else {
        next.set(productId, { productId, roleId });
      }
      return next;
    });
  }, []);

  const submit = useCallback(
    async (formData: UserFormData) => {
      setProcessing(true);
      try {
        await addUser({
          variables: {
            input: {
              displayName: formData.displayName,
              email: formData.email,
              memo: formData.memo,
              organizationShortCode: formData.organizationShortCode,
              productRoleAssignments: Array.from(selectedRoles.values()),
              userSegmentIds: formData.userSegmentIds,
              userGroupIds: formData.userGroupIds,
            },
          },
        });
        toast.success(t("feature.common.createdSuccessfully", { model: "user" }));
        onSuccess?.(formData);
      } catch (e) {
        const saved =
          e instanceof Error
            ? /User ([\da-f-]+) was saved, but COTEN ID login preparation failed/.exec(e.message)
            : null;
        if (saved) {
          await client.refetchQueries({ include: [GetUsersDocument] }).catch(() => undefined);
          notifySaved(saved[1]);
          onSuccess?.(formData);
        } else {
          toast.error(translateError(t, e));
        }
      } finally {
        setProcessing(false);
      }
    },
    [addUser, client, selectedRoles, t, onSuccess, notifySaved],
  );

  return { submit, processing, selectedRoles, setRoleForProduct };
}
